import { useState, useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Plus,
  Stethoscope,
  Loader2,
  Calendar,
  ChevronRight,
  PanelRightOpen,
  PanelRightClose,
  FlaskConical,
  ExternalLink,
  Pill,
  Printer,
  Download,
  Pencil,
  X,
} from 'lucide-react'

import { Button } from '@components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@components/ui/dialog'
import { Input } from '@components/ui/input'
import { Label } from '@components/ui/label'
import { useActivePatient } from '@context/ActivePatientContext'
import { useClinicFormat } from '@context/ClinicTimezoneContext'
import { useToast } from '@hooks/use-toast'
import { useConsultations } from '@hooks/useConsultations'
import { ConsultationForm } from '@components/consultation/ConsultationForm'
import { HistoriaClinicaPanel } from '@components/consultation/HistoriaClinicaPanel'
import { useAuthStore } from '@hooks/useAuthStore'
import { usePermissions } from '@hooks/usePermissions'
import { appointmentApi } from '@services/appointment.service'
import { pharmacyApi } from '@services/pharmacy.service'
import { getApiErrorMessage } from '@services/api'
import { cn } from '@lib/utils'
import { getConsultationsByPatient, type ConsultationSummary } from '@services/consultation.service'
import type { Appointment } from '@/types'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@components/ui/select'

const appointmentTypeLabels: Record<string, string> = {
  consultation: 'Consulta',
  follow_up: 'Seguimiento',
  emergency: 'Emergencia',
  routine_checkup: 'Revisión rutinaria',
  vaccination: 'Vacunación',
  lab_test: 'Laboratorio',
  imaging: 'Imagen',
  procedure: 'Procedimiento',
  telemedicine: 'Telemedicina',
}

const appointmentStatusLabels: Record<string, string> = {
  scheduled: 'Programada',
  confirmed: 'Confirmada',
  checked_in: 'En recepción',
  in_progress: 'En curso',
  completed: 'Completada',
  cancelled: 'Cancelada',
  no_show: 'No asistió',
  rescheduled: 'Reprogramada',
}

const encounterCapableAppointmentTypes = new Set([
  'consultation',
  'follow_up',
  'emergency',
  'telemedicine',
])

import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs"

export function ActiveConsultationView({
  initialAppointmentId = "none",
  embedded = false,
}: {
  initialAppointmentId?: string
  /**
   * When hosted inside the two-zone active-consultation screen, the read-only
   * historia-clínica context lives in Zone A, so this view drops its own title
   * bar + right-side context panel and renders the editable note full-width.
   */
  embedded?: boolean
}) {
  const { activePatientData } = useActivePatient()
  const { formatDate } = useClinicFormat()
  const { toast } = useToast()
  const navigate = useNavigate()
    const [selectedAppointmentId, setSelectedAppointmentId] = useState<string>(initialAppointmentId)
    const [consultationLabs, setConsultationLabs] = useState<any[]>([])
  const [consultationAccessError, setConsultationAccessError] = useState<string | null>(null)

  // Prescription panel state
  const [consultationRx, setConsultationRx] = useState<any[]>([])
  const [loadingRx, setLoadingRx] = useState(false)

  // New prescription dialog state
  const [newRxOpen, setNewRxOpen] = useState(false)
  const [editingPrescriptionSetId, setEditingPrescriptionSetId] = useState<string | null>(null)
  const [editingPrescriptionSetLabel, setEditingPrescriptionSetLabel] = useState<string | null>(null)
  const [rxMedSearchOpen, setRxMedSearchOpen] = useState(false)
  const [draftEditingIndex, setDraftEditingIndex] = useState<number | null>(null)
  const [rxMedSearch, setRxMedSearch] = useState('')
  const [rxMedications, setRxMedications] = useState<any[]>([])
  const [rxDraftItems, setRxDraftItems] = useState<any[]>([])
  const [rxForm, setRxForm] = useState({
    medication_id: '',
    medication_name: '',
    dosage: '',
    route: 'oral',
    frequency: '',
    duration: '',
    quantity: 1,
    quantity_unit: 'tabletas',
    refills: 0,
    instructions: '',
    recetario_folio: '',
    requires_recetario_especial: false,
  })
  const [creatingRx, setCreatingRx] = useState(false)

  const [panelOpen, setPanelOpen] = useState(() => {
    try {
      return localStorage.getItem('hms-hc-panel') !== 'closed'
    } catch {
      return true
    }
  })

  const togglePanel = () => {
    setPanelOpen((prev) => {
      const next = !prev
      try { localStorage.setItem('hms-hc-panel', next ? 'open' : 'closed') } catch {}
      return next
    })
  }

  // Timeline data
  const [, setConsultations] = useState<ConsultationSummary[]>([])
  const [appointments, setAppointments] = useState<Appointment[]>([])
  
  // Consultation integration
  const { user } = useAuthStore()
  const { isOwner } = usePermissions()
  const { useConsultationByAppointment, useCreateConsultation } = useConsultations()
  const consultationQuery = useConsultationByAppointment(
    selectedAppointmentId !== 'none' ? selectedAppointmentId : ''
  )
  const createConsultation = useCreateConsultation()
  const [creatingConsultation, setCreatingConsultation] = useState(false)

  const handleStartConsultation = async () => {
    if (selectedAppointmentId === 'none') return
    setCreatingConsultation(true)
    try {
      await createConsultation.mutateAsync({ appointment_id: selectedAppointmentId })
      toast({
        title: 'Consulta iniciada',
        description: 'Se creó una nueva consulta para esta cita.',
      })
      consultationQuery.refetch()
      loadTimeline()
    } catch (error: any) {
      const detail = getApiErrorMessage(error, 'No fue posible crear la consulta.')
      if (detail.toLowerCase().includes('ya tiene una consulta')) {
        toast({
          title: 'La consulta ya existe',
          description: detail,
          variant: 'destructive',
        })
      } else {
        toast({
          title: 'No pudimos iniciar la consulta',
          description: detail,
          variant: 'destructive',
        })
      }
    } finally {
      setCreatingConsultation(false)
    }
  }

  const loadAppointments = useCallback(async () => {
    if (!activePatientData?.id) return
    try {
      const aptsRes = await appointmentApi.getAppointments({ patient_id: activePatientData.id, limit: 100 })
      setAppointments(aptsRes?.items ?? [])
    } catch (err) {
      console.error('Failed to load appointments:', err)
      setAppointments([])
    }
  }, [activePatientData?.id])

  const loadTimeline = useCallback(async () => {
    if (!activePatientData?.id) return
    try {
      const list = await getConsultationsByPatient(activePatientData.id, 50)
      setConsultations(list ?? [])
    } catch (err) {
      console.error('Failed to load consultations:', err)
      setConsultations([])
    }
    await loadAppointments()
  }, [activePatientData?.id, loadAppointments])

  useEffect(() => {
    loadAppointments()
  }, [loadAppointments])

  useEffect(() => {
    if (!consultationQuery.error) {
      setConsultationAccessError(null)
      return
    }

    const detail = getApiErrorMessage(consultationQuery.error as any, 'No se pudo cargar la consulta.')
    setConsultationAccessError(detail)
    toast({
      title: 'No se pudo abrir la consulta',
      description: detail,
      variant: 'destructive',
    })
  }, [consultationQuery.error, toast])

  useEffect(() => {
    const consultationId = consultationQuery.data?.id
    if (!consultationId) {
      setConsultationLabs([])
      return
    }
    let ignore = false
    pharmacyApi
      .getLabResults({ consultation_id: consultationId, limit: 20 })
      .then((res) => {
        if (ignore) return
        setConsultationLabs(res.items ?? [])
      })
      .catch((err) => {
        if (ignore) return
        console.error('[ConsultaTab] Failed to load labs for consultation:', err)
        setConsultationLabs([])
      })
    return () => { ignore = true }
  }, [consultationQuery.data?.id])

  // Load prescriptions for current consultation
  useEffect(() => {
    const consultationId = consultationQuery.data?.id
    if (!consultationId) { setConsultationRx([]); return }
    let ignore = false
    setLoadingRx(true)
    pharmacyApi.getPrescriptions({ consultation_id: consultationId, limit: 50 })
      .then((res) => { if (!ignore) setConsultationRx(res.items ?? []) })
      .catch((err) => { if (!ignore) { console.error('[ConsultaTab] Failed to load prescriptions:', err); setConsultationRx([]) } })
      .finally(() => { if (!ignore) setLoadingRx(false) })
    return () => { ignore = true }
  }, [consultationQuery.data?.id])

  // Load medications catalog when dialog opens
  useEffect(() => {
    if (!newRxOpen) return
    pharmacyApi.getMedications('', 0, 500)
      .then((res) => setRxMedications(res.items ?? []))
      .catch(() => setRxMedications([]))
  }, [newRxOpen])

  const resetRxForm = () => {
    setRxForm({ medication_id: '', medication_name: '', dosage: '', route: 'oral', frequency: '', duration: '', quantity: 1, quantity_unit: 'tabletas', refills: 0, instructions: '', recetario_folio: '', requires_recetario_especial: false })
    setRxMedSearch('')
    setRxMedSearchOpen(false)
    setDraftEditingIndex(null)
  }

  const resetPrescriptionSetEditor = () => {
    setEditingPrescriptionSetId(null)
    setEditingPrescriptionSetLabel(null)
    setRxDraftItems([])
    resetRxForm()
  }

  useEffect(() => {
    if (draftEditingIndex === null || !newRxOpen) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        resetRxForm()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [draftEditingIndex, newRxOpen])

  const consultationPrescriptionSets = Object.values(
    consultationRx.reduce((acc: Record<string, any>, rx: any) => {
      const key = rx.prescription_set_id || rx.id
      if (!acc[key]) {
        acc[key] = {
          id: key,
          label: rx.prescription_set_label || rx.prescription_number || `Receta ${Object.keys(acc).length + 1}`,
          items: [],
        }
      }
      acc[key].items.push(rx)
      return acc
    }, {})
  )

  const openPrescriptionSetPdf = async (prescriptionSetId?: string | null) => {
    if (!prescriptionSetId) return null
    const blob = await pharmacyApi.downloadPrescriptionSetPdf(prescriptionSetId)
    return URL.createObjectURL(blob)
  }

  const handleSaveDraftMedication = () => {
    if (!rxForm.medication_id || !rxForm.dosage || !rxForm.frequency || !rxForm.duration) {
      toast({ title: 'Datos incompletos', description: 'Completa medicamento, dosis, frecuencia y duración.', variant: 'destructive' })
      return
    }

    if (rxForm.requires_recetario_especial && !rxForm.recetario_folio.trim()) {
      toast({ title: 'Folio requerido', description: 'Este medicamento controlado requiere el folio del recetario especial (COFEPRIS).', variant: 'destructive' })
      return
    }

    const item = {
      ...rxForm,
      _key: draftEditingIndex !== null ? rxDraftItems[draftEditingIndex]?._key : crypto.randomUUID(),
      id: draftEditingIndex !== null ? rxDraftItems[draftEditingIndex]?.id : undefined,
    }

    setRxDraftItems((prev) => {
      if (draftEditingIndex === null) return [...prev, item]
      return prev.map((entry, index) => (index === draftEditingIndex ? item : entry))
    })
    resetRxForm()
  }

  const handleEditDraftMedication = (index: number) => {
    const item = rxDraftItems[index]
    setDraftEditingIndex(index)
    setRxForm({
      medication_id: item.medication_id,
      medication_name: item.medication_name,
      dosage: item.dosage,
      route: item.route,
      frequency: item.frequency,
      duration: item.duration,
      quantity: item.quantity,
      quantity_unit: item.quantity_unit,
      refills: item.refills ?? 0,
      instructions: item.instructions ?? '',
      recetario_folio: item.recetario_folio ?? '',
      requires_recetario_especial: item.requires_recetario_especial ?? false,
    })
    setRxMedSearch(item.medication_name)
    setRxMedSearchOpen(false)
  }

  const handleRemoveDraftMedication = (index: number) => {
    setRxDraftItems((prev) => prev.filter((_, itemIndex) => itemIndex !== index))
    if (draftEditingIndex === index) resetRxForm()
  }

  const handleRxMedSelect = (med: any) => {
    setRxForm(prev => ({
      ...prev,
      medication_id: med.id,
      medication_name: med.brand_name || med.generic_name,
      dosage: `${med.strength} ${med.strength_unit}`,
      quantity_unit: med.dosage_form === 'tablet' ? 'tabletas' : med.dosage_form === 'capsule' ? 'cápsulas' : med.dosage_form || 'unidades',
      requires_recetario_especial: !!med.requires_recetario_especial,
      recetario_folio: med.requires_recetario_especial ? prev.recetario_folio : '',
    }))
    setRxMedSearch(med.brand_name || med.generic_name)
    setRxMedSearchOpen(false)
  }

  const handleOpenNewPrescriptionSet = () => {
    resetPrescriptionSetEditor()
    setNewRxOpen(true)
  }

  const handleEditPrescriptionSet = (setGroup: any) => {
    setEditingPrescriptionSetId(setGroup.id)
    setEditingPrescriptionSetLabel(setGroup.label)
    setRxDraftItems(setGroup.items.map((rx: any) => ({ ...rx, _key: rx.id })))
    resetRxForm()
    setNewRxOpen(true)
  }

  const handleSavePrescriptionSet = async () => {
    if (!consultationQuery.data || !activePatientData) return
    if (rxDraftItems.length === 0) {
      toast({ title: 'Receta vacía', description: 'Agrega al menos un medicamento al conjunto antes de guardar.', variant: 'destructive' })
      return
    }

    setCreatingRx(true)
    try {
      const existingSetItems = editingPrescriptionSetId
        ? consultationRx.filter((rx: any) => (rx.prescription_set_id || rx.id) === editingPrescriptionSetId)
        : []
      const existingIds = new Set(existingSetItems.map((rx: any) => rx.id))
      const keptIds = new Set(rxDraftItems.map((item: any) => item.id).filter(Boolean))
      const setId = editingPrescriptionSetId || crypto.randomUUID()
      const setLabel = editingPrescriptionSetLabel || `Receta ${consultationPrescriptionSets.length + 1}`

      for (const item of rxDraftItems) {
        const payload = {
          patient_id: activePatientData.id,
          appointment_id: consultationQuery.data.appointment_id,
          consultation_id: consultationQuery.data.id,
          prescription_set_id: setId,
          prescription_set_label: setLabel,
          medication_id: item.medication_id,
          medication_name: item.medication_name,
          dosage: item.dosage,
          route: item.route,
          frequency: item.frequency,
          duration: item.duration,
          quantity: item.quantity,
          quantity_unit: item.quantity_unit,
          refills: item.refills,
          instructions: item.instructions || undefined,
          recetario_folio: item.recetario_folio || undefined,
        }

        if (item.id) {
          await pharmacyApi.updatePrescription(item.id, payload as any)
        } else {
          await pharmacyApi.createPrescription(payload as any)
        }
      }

      for (const existingId of Array.from(existingIds)) {
        if (!keptIds.has(existingId)) {
          await pharmacyApi.deletePrescription(existingId)
        }
      }

      const res = await pharmacyApi.getPrescriptions({ consultation_id: consultationQuery.data.id, limit: 50 })
      setConsultationRx(res.items ?? [])
      setNewRxOpen(false)
      resetPrescriptionSetEditor()
      toast({
        title: editingPrescriptionSetId ? 'Receta actualizada' : 'Receta creada',
        description: editingPrescriptionSetId
          ? 'Se actualizó el conjunto de medicamentos de la receta.'
          : 'Se creó una nueva receta con su conjunto de medicamentos.',
      })
    } catch (err: any) {
      console.error('[ConsultaTab] Failed to save prescription set:', err)
      toast({ title: 'Error', description: getApiErrorMessage(err, 'No se pudo guardar la receta.'), variant: 'destructive' })
    } finally {
      setCreatingRx(false)
    }
  }

  const handlePrintPrescription = async (prescriptionSetId?: string | null) => {
    try {
      const url = await openPrescriptionSetPdf(prescriptionSetId)
      if (!url) return

      const printWindow = window.open('', '_blank', 'noopener,noreferrer,width=1024,height=768')
      if (!printWindow) {
        toast({
          title: 'Ventana bloqueada',
          description: 'Permite ventanas emergentes para abrir la vista de impresión de la receta.',
          variant: 'destructive',
        })
        URL.revokeObjectURL(url)
        return
      }

      printWindow.document.write(`<!doctype html><html><head><title>Imprimir receta</title><style>html,body{margin:0;height:100%;background:#111;}iframe{border:0;width:100%;height:100%;}</style></head><body><iframe src="${url}"></iframe><script>const frame=document.querySelector('iframe');frame.addEventListener('load',()=>{setTimeout(()=>{window.focus();window.print();},700);});window.addEventListener('afterprint',()=>window.close());</script></body></html>`)
      printWindow.document.close()
      setTimeout(() => URL.revokeObjectURL(url), 60_000)
    } catch (err) {
      console.error('[ConsultaTab] Failed to print prescription:', err)
      toast({ title: 'Error', description: 'No se pudo imprimir la receta.', variant: 'destructive' })
    }
  }

  const handleDownloadPrescription = async (prescriptionSetId?: string | null, label?: string | null) => {
    if (!prescriptionSetId) return
    try {
      const blob = await pharmacyApi.downloadPrescriptionSetPdf(prescriptionSetId)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${(label || 'receta').replace(/\s+/g, '_').toLowerCase()}.pdf`
      a.click()
      URL.revokeObjectURL(url)
    } catch (err) {
      console.error('[ConsultaTab] Failed to download prescription PDF:', err)
      toast({ title: 'Error', description: 'No se pudo descargar la receta.', variant: 'destructive' })
    }
  }


  if (!activePatientData) return null


  const selectedAppointment =
    selectedAppointmentId !== 'none'
      ? appointments.find((appointment) => appointment.id === selectedAppointmentId) ?? null
      : null

  const canStartConsultation =
    !!selectedAppointment &&
    ['confirmed', 'checked_in', 'in_progress'].includes(selectedAppointment.status) &&
    encounterCapableAppointmentTypes.has(selectedAppointment.appointment_type) &&
    (
      selectedAppointment.appointment_type !== 'follow_up' ||
      !!selectedAppointment.origin_consultation_id
    )

  const handleAppointmentStatusUpdate = async (status: 'confirmed' | 'checked_in') => {
    if (!selectedAppointment) return

    try {
      await appointmentApi.updateAppointment(selectedAppointment.id, { status })
      toast({
        title: 'Estado actualizado',
        description: `La cita quedó marcada como ${appointmentStatusLabels[status].toLowerCase()}.`,
      })
      await loadTimeline()
    } catch (error: any) {
      toast({
        title: 'No pudimos actualizar la cita',
        description: getApiErrorMessage(error, 'No fue posible actualizar el estado de la cita.'),
        variant: 'destructive',
      })
    }
  }


  // Inside the two-zone screen the historia-clínica side panel is replaced by
  // Zone A, so it is never shown when embedded.
  const showPanel = panelOpen && !embedded

  // === DEFAULT VIEW: Timeline ===
  // === ACTIVE CONSULTATION VIEW ===
  return (
    <div className="animate-fade-in">
      {/* Top bar */}
      <div className={cn('flex items-center mb-8', embedded ? 'justify-end' : 'justify-between')}>
        {!embedded && (
          <div className="flex items-center gap-3">
            <Stethoscope className="h-5 w-5 text-ml-primary" />
            <h2 className="text-2xl font-headline italic text-ml-primary">
              Nueva Consulta
            </h2>
          </div>
        )}
        <div className="flex items-center gap-2">
          {!embedded && (
            <Button
              variant="ghost"
              size="sm"
              onClick={togglePanel}
              className="rounded text-ml-on-surface-variant hover:text-ml-primary"
              title={panelOpen ? 'Ocultar historia clínica' : 'Mostrar historia clínica'}
            >
              {panelOpen ? (
                <PanelRightClose className="h-4 w-4" />
              ) : (
                <PanelRightOpen className="h-4 w-4" />
              )}
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => setSelectedAppointmentId('none')}
            className="rounded"
          >
            Cancelar
          </Button>
        </div>
      </div>

      {/* Main layout: form + side panel */}
      <div className={cn('flex gap-6', showPanel ? 'flex-row' : 'flex-col')}>
        {/* Left: consultation content */}
        <div className={cn('space-y-8 min-w-0', showPanel ? 'flex-[3]' : 'w-full')}>
          {/* Appointment Selector */}
          <section className="bg-ml-surface-container-lowest rounded-2xl p-8 border border-ml-outline-variant/5">
            <div className="flex items-center gap-2 mb-4">
              <Calendar className="h-5 w-5 text-ml-primary" />
              <h3 className="text-lg font-headline italic text-ml-primary">
                Vincular a Cita
              </h3>
            </div>
            <Select
              value={selectedAppointmentId}
              onValueChange={setSelectedAppointmentId}
            >
              <SelectTrigger className="w-full max-w-md">
                <SelectValue placeholder="Seleccionar cita..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Seleccionar cita...</SelectItem>
                {appointments
                  .filter((a) => a.status !== 'cancelled')
                  .sort((a, b) => new Date(b.appointment_date).getTime() - new Date(a.appointment_date).getTime())
                  .slice(0, 20)
                  .map((apt) => (
                    <SelectItem key={apt.id} value={apt.id}>
                      {formatDate(apt.appointment_date)}{' '}
                      - {appointmentTypeLabels[apt.appointment_type] ?? apt.appointment_type} - {appointmentStatusLabels[apt.status] ?? apt.status}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </section>

          {/* Consultation Integration */}
          {selectedAppointmentId === 'none' ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <Calendar className="h-12 w-12 text-ml-on-surface-variant/20 mb-4" />
              <p className="text-sm text-ml-on-surface-variant">
                Seleccione una cita para iniciar la consulta.
              </p>
            </div>
          ) : consultationQuery.isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-ml-primary" />
            </div>
          ) : consultationAccessError ? (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 text-left max-w-2xl">
              <p className="text-sm font-semibold text-amber-900">No se pudo abrir la consulta</p>
              <p className="text-sm text-amber-800 mt-1">{consultationAccessError}</p>
              <p className="text-xs text-amber-700/90 mt-3">
                Tu sesión sigue activa. Este mensaje indica un problema de permisos o alcance para esta consulta específica.
              </p>
            </div>
          ) : consultationQuery.data ? (
            <>
              <Tabs defaultValue="clinica">
                <TabsList className="w-full justify-start border-b rounded-none px-0 h-auto bg-transparent">
                  <TabsTrigger value="clinica" className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-ml-primary rounded-none">Clínica</TabsTrigger>
                  <TabsTrigger value="laboratorios" className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-ml-primary rounded-none">Laboratorios</TabsTrigger>
                  <TabsTrigger value="recetas" className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-ml-primary rounded-none">Recetas</TabsTrigger>
                </TabsList>
                <TabsContent value="clinica" className="pt-6">
                  <ConsultationForm
                    consultation={consultationQuery.data}
                    isAdmin={isOwner || user?.role === 'admin' || user?.role === 'super_admin'}
                    isAuthor={consultationQuery.data.doctor_id === user?.id}
                    onSaved={() => {
                      consultationQuery.refetch()
                      loadTimeline()
                    }}
                  />
                </TabsContent>
                
                <TabsContent value="laboratorios" className="pt-6">
                <div className="bg-ml-surface-container-low/30 rounded-xl border border-ml-outline-variant/10 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <FlaskConical className="h-4 w-4 text-ml-primary" />
                      <span className="text-sm font-semibold text-ml-on-surface">
                        Laboratorios de esta consulta
                      </span>
                      {consultationLabs.length > 0 && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-ml-primary-fixed text-ml-primary font-bold">
                          {consultationLabs.length}
                        </span>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-xs text-ml-primary h-7 px-2"
                      onClick={() =>
                        navigate(`/workspace/patient/${activePatientData?.id}/laboratorios`)
                      }
                    >
                      <ExternalLink className="h-3.5 w-3.5 mr-1" />
                      Ver todos
                    </Button>
                  </div>

                  {consultationLabs.length === 0 ? (
                    <p className="text-xs text-ml-on-surface-variant/60 italic py-2">
                      Sin laboratorios ordenados en esta consulta
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {consultationLabs.map((lab: any) => (
                        <button
                          key={lab.id}
                          className="w-full text-left flex items-center justify-between p-2 rounded-lg hover:bg-ml-surface-container-low transition-colors group"
                          onClick={() =>
                            navigate(`/workspace/patient/${activePatientData?.id}/laboratorios`)
                          }
                        >
                          <div>
                            <p className="text-xs font-medium text-ml-on-surface">{lab.test_name}</p>
                            <p className="text-[10px] text-ml-on-surface-variant/60 mt-0.5">
                              {lab.accession_number} · {formatDate(lab.ordered_date)}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={cn(
                              'text-[10px] px-1.5 py-0.5 rounded capitalize',
                              lab.status === 'completed' || lab.status === 'reviewed'
                                ? 'bg-green-100 text-green-700'
                                : 'bg-ml-surface-container-high text-ml-on-surface-variant'
                            )}>
                              {lab.status}
                            </span>
                            <ChevronRight className="h-3.5 w-3.5 text-ml-on-surface-variant/40 group-hover:text-ml-primary transition-colors" />
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                </TabsContent>

                <TabsContent value="recetas" className="pt-6">
                <div className="bg-ml-surface-container-low/30 rounded-xl border border-ml-outline-variant/10 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Pill className="h-4 w-4 text-ml-primary" />
                      <span className="text-sm font-semibold text-ml-on-surface">
                        Conjunto de medicamentos de esta receta
                      </span>
                      {consultationPrescriptionSets.length > 0 && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-ml-primary-fixed text-ml-primary font-bold">
                          {consultationPrescriptionSets.length}
                        </span>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-xs text-ml-primary h-7 px-2"
                      onClick={handleOpenNewPrescriptionSet}
                    >
                      <Plus className="h-3.5 w-3.5 mr-1" />
                      Nueva Receta
                    </Button>
                  </div>

                  {loadingRx ? (
                    <div className="flex items-center justify-center py-3">
                      <Loader2 className="h-4 w-4 animate-spin text-ml-primary" />
                    </div>
                  ) : consultationPrescriptionSets.length === 0 ? (
                    <p className="text-xs text-ml-on-surface-variant/60 italic py-2">
                      Sin recetas en esta consulta
                    </p>
                  ) : (
                    <>
                      <div className="space-y-3 mb-3">
                        {consultationPrescriptionSets.map((setGroup: any) => (
                          <div key={setGroup.id} className="rounded-lg bg-ml-surface-container-low/40 p-3">
                            <div className="flex items-center justify-between gap-3 mb-2">
                              <div>
                                <p className="text-xs font-semibold text-ml-on-surface">{setGroup.label}</p>
                                <p className="text-[10px] text-ml-on-surface-variant/70 mt-0.5">{setGroup.items.length} medicamento(s)</p>
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-xs text-ml-primary h-7 px-2 shrink-0"
                                onClick={() => handleEditPrescriptionSet(setGroup)}
                              >
                                <Pencil className="h-3.5 w-3.5 mr-1" />
                                Editar receta
                              </Button>
                            </div>
                            <div className="space-y-2">
                              {setGroup.items.map((rx: any) => (
                                <div key={rx.id} className="flex items-center justify-between gap-3 p-2 rounded-lg bg-white/60">
                                  <div className="min-w-0">
                                    <p className="text-xs font-medium text-ml-on-surface">{rx.medication_name}</p>
                                    <p className="text-[10px] text-ml-on-surface-variant/60 mt-0.5">
                                      {rx.dosage} · {rx.frequency} · {rx.duration} · {rx.quantity} {rx.quantity_unit}
                                    </p>
                                  </div>
                                </div>
                              ))}
                            </div>
                            <div className="flex flex-wrap items-center gap-2 pt-3 mt-3 border-t border-ml-outline-variant/10">
                              <Button variant="ghost" size="sm" className="text-xs text-ml-primary h-7 px-2" onClick={() => handlePrintPrescription(setGroup.id)}>
                                <Printer className="h-3.5 w-3.5 mr-1" />
                                Imprimir
                              </Button>
                              <Button variant="ghost" size="sm" className="text-xs text-ml-primary h-7 px-2" onClick={() => handleDownloadPrescription(setGroup.id, setGroup.label)}>
                                <Download className="h-3.5 w-3.5 mr-1" />
                                Descargar PDF
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="rounded-lg bg-ml-surface-container-low/40 px-3 py-2 text-[11px] text-ml-on-surface-variant mb-3">
                        Cada consulta puede tener varias recetas. Cada receta guarda su propio conjunto de medicamentos y se puede editar en un solo paso.
                      </div>
                    </>
                  )}
                </div>
                </TabsContent>
              </Tabs>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <Stethoscope className="h-12 w-12 text-ml-on-surface-variant/20 mb-4" />
              <p className="text-sm text-ml-on-surface-variant mb-6">
                No existe una consulta para esta cita todavía.
              </p>
              {selectedAppointment && !canStartConsultation ? (
                <>
                  {!encounterCapableAppointmentTypes.has(selectedAppointment.appointment_type) ? (
                    <p className="text-sm text-ml-on-surface-variant mb-6 max-w-xl">
                      La cita seleccionada es de tipo <strong>{appointmentTypeLabels[selectedAppointment.appointment_type] ?? selectedAppointment.appointment_type}</strong> y no puede abrir una consulta clínica. Usa una cita de <strong>Consulta</strong>, <strong>Seguimiento</strong>, <strong>Emergencia</strong> o <strong>Telemedicina</strong>.
                    </p>
                  ) : selectedAppointment.appointment_type === 'follow_up' && !selectedAppointment.origin_consultation_id ? (
                    <p className="text-sm text-ml-on-surface-variant mb-6 max-w-xl">
                      Esta cita está marcada como <strong>Seguimiento</strong>, pero todavía no tiene una consulta origen vinculada. Edítala desde citas y selecciona la consulta inicial completada antes de iniciar la atención.
                    </p>
                  ) : (
                    <>
                      <p className="text-sm text-ml-on-surface-variant mb-6 max-w-xl">
                        La cita está en estado <strong>{appointmentStatusLabels[selectedAppointment.status] ?? selectedAppointment.status}</strong>. Para iniciar la consulta, primero debe quedar como <strong>Confirmada</strong> o <strong>En recepción</strong>.
                      </p>
                      <div className="flex flex-wrap items-center justify-center gap-3">
                        {selectedAppointment.status === 'scheduled' && (
                          <Button
                            onClick={() => handleAppointmentStatusUpdate('confirmed')}
                            variant="outline"
                            className="rounded"
                          >
                            Confirmar cita
                          </Button>
                        )}
                        {['scheduled', 'confirmed'].includes(selectedAppointment.status) && (
                          <Button
                            onClick={() => handleAppointmentStatusUpdate('checked_in')}
                            className="bg-ml-primary text-ml-on-primary hover:bg-ml-primary/90 rounded px-8 font-bold shadow-lg shadow-ml-primary/10"
                          >
                            Registrar llegada
                          </Button>
                        )}
                      </div>
                    </>
                  )}
                </>
              ) : (
                <Button
                  onClick={handleStartConsultation}
                  disabled={creatingConsultation}
                  className="bg-ml-primary text-ml-on-primary hover:bg-ml-primary/90 rounded px-8 font-bold shadow-lg shadow-ml-primary/10"
                >
                  {creatingConsultation ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Iniciando...
                    </>
                  ) : (
                    <>
                      <Stethoscope className="mr-2 h-4 w-4" />
                      Iniciar Consulta
                    </>
                  )}
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Right: Historia Clínica side panel */}
        {showPanel && (
          <aside className="flex-[2] min-w-[280px] max-w-[400px] bg-ml-surface-container-lowest rounded-2xl p-5 border border-ml-outline-variant/5 h-fit sticky top-4 overflow-y-auto max-h-[calc(100vh-8rem)]">
            <HistoriaClinicaPanel patientId={activePatientData.id} />
          </aside>
        )}
      </div>

      {/* New Prescription Dialog */}
      <Dialog open={newRxOpen} onOpenChange={(open) => { if (!open) resetRxForm(); setNewRxOpen(open) }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto p-0 gap-0 border-0 shadow-2xl">
          <div className="sticky top-0 z-10 bg-ml-surface-container-lowest border-b border-ml-outline-variant/10 px-8 py-6 flex items-start justify-between">
            <div>
              <DialogTitle className="text-2xl font-headline italic text-ml-primary mb-1">
                {editingPrescriptionSetId ? 'Editar receta' : 'Nueva receta'}
              </DialogTitle>
              <DialogDescription className="text-sm">
                Crea o edita un conjunto de medicamentos en un solo paso.
              </DialogDescription>
            </div>
            {editingPrescriptionSetLabel && (
              <div className="hidden sm:inline-flex items-center gap-2 rounded bg-ml-surface-container-low px-4 py-2 text-sm text-ml-on-surface-variant border border-ml-outline-variant/10">
                <span className="w-2.5 h-2.5 rounded-full bg-ml-primary animate-pulse"></span>
                Editando <span className="font-semibold text-ml-on-surface">{editingPrescriptionSetLabel}</span>
              </div>
            )}
          </div>
          
          <div className="p-8 grid grid-cols-1 md:grid-cols-[1.2fr_1fr] gap-10 bg-[#FAFAFA]">
            <div className="space-y-5">
              <div className="bg-ml-surface-container-lowest border border-ml-outline-variant/20 rounded-xl p-5 shadow-sm">
                <h3 className="text-sm font-semibold text-ml-on-surface flex items-center gap-2 mb-4">
                  <Pill className="w-4 h-4 text-ml-primary" />
                  {draftEditingIndex !== null ? 'Editar medicamento' : 'Agregar medicamento'}
                </h3>
                
                <div className="space-y-4">
                  {/* Medication search */}
                  <div>
                    <div className="flex items-center justify-between gap-3 mb-1.5">
                      <Label className="text-xs font-medium text-ml-on-surface-variant uppercase tracking-wider block">Medicamento</Label>
                      {draftEditingIndex !== null && (
                        <span className="text-[10px] font-medium uppercase tracking-wider text-ml-on-surface-variant/70">
                          No editable al actualizar
                        </span>
                      )}
                    </div>
                    <div className="relative">
                      <Input
                        placeholder="Buscar medicamento..."
                        value={rxMedSearch}
                        readOnly={draftEditingIndex !== null}
                        onFocus={() => {
                          if (draftEditingIndex === null) setRxMedSearchOpen(true)
                        }}
                        onBlur={() => window.setTimeout(() => setRxMedSearchOpen(false), 150)}
                        onChange={(e) => {
                          if (draftEditingIndex !== null) return
                          setRxMedSearch(e.target.value)
                          setRxMedSearchOpen(true)
                        }}
                        className="bg-white border-ml-outline-variant/20 focus-visible:ring-ml-primary read-only:bg-ml-surface-container-low read-only:text-ml-on-surface-variant"
                      />
                      {rxMedSearchOpen && draftEditingIndex === null && rxMedSearch.length >= 2 && (
                        <div className="absolute z-50 w-full mt-1 max-h-48 overflow-y-auto border border-ml-outline-variant/20 rounded-lg bg-ml-surface-container-lowest shadow-xl">
                          {rxMedications
                            .filter((m: any) =>
                              (m.brand_name?.toLowerCase().includes(rxMedSearch.toLowerCase()) ||
                               m.generic_name?.toLowerCase().includes(rxMedSearch.toLowerCase()))
                            )
                            .slice(0, 10)
                            .map((m: any) => (
                              <button
                                key={m.id}
                                type="button"
                                className="w-full text-left px-4 py-2.5 text-sm hover:bg-ml-surface-container transition-colors border-b last:border-b-0 border-ml-outline-variant/10"
                                onClick={() => handleRxMedSelect(m)}
                              >
                                <span className="font-semibold text-ml-on-surface">{m.brand_name}</span>
                                {m.generic_name && <span className="text-ml-on-surface-variant ml-1.5 font-medium">({m.generic_name})</span>}
                                <span className="block text-[10px] text-ml-on-surface-variant/70 mt-0.5 uppercase tracking-wider">{m.strength} {m.strength_unit} · {m.dosage_form}</span>
                              </button>
                            ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs font-medium text-ml-on-surface-variant uppercase tracking-wider mb-1.5 block">Dosis</Label>
                      <Input value={rxForm.dosage} onChange={(e) => setRxForm(p => ({ ...p, dosage: e.target.value }))} placeholder="ej. 500mg" className="bg-white" />
                    </div>
                    <div>
                      <Label className="text-xs font-medium text-ml-on-surface-variant uppercase tracking-wider mb-1.5 block">Vía</Label>
                      <Select value={rxForm.route} onValueChange={(v) => setRxForm(p => ({ ...p, route: v }))}>
                        <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="oral">Oral</SelectItem>
                          <SelectItem value="iv">IV</SelectItem>
                          <SelectItem value="im">IM</SelectItem>
                          <SelectItem value="subcutanea">Subcutánea</SelectItem>
                          <SelectItem value="topica">Tópica</SelectItem>
                          <SelectItem value="inhalada">Inhalada</SelectItem>
                          <SelectItem value="rectal">Rectal</SelectItem>
                          <SelectItem value="sublingual">Sublingual</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs font-medium text-ml-on-surface-variant uppercase tracking-wider mb-1.5 block">Frecuencia</Label>
                      <Input value={rxForm.frequency} onChange={(e) => setRxForm(p => ({ ...p, frequency: e.target.value }))} placeholder="ej. cada 8 horas" className="bg-white" />
                    </div>
                    <div>
                      <Label className="text-xs font-medium text-ml-on-surface-variant uppercase tracking-wider mb-1.5 block">Duración</Label>
                      <Input value={rxForm.duration} onChange={(e) => setRxForm(p => ({ ...p, duration: e.target.value }))} placeholder="ej. 7 días" className="bg-white" />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label className="text-xs font-medium text-ml-on-surface-variant uppercase tracking-wider mb-1.5 block">Cantidad</Label>
                      <Input type="number" min={1} value={rxForm.quantity} onChange={(e) => setRxForm(p => ({ ...p, quantity: Number(e.target.value) || 1 }))} className="bg-white" />
                    </div>
                    <div>
                      <Label className="text-xs font-medium text-ml-on-surface-variant uppercase tracking-wider mb-1.5 block">Unidad</Label>
                      <Input value={rxForm.quantity_unit} onChange={(e) => setRxForm(p => ({ ...p, quantity_unit: e.target.value }))} className="bg-white" />
                    </div>
                    <div>
                      <Label className="text-xs font-medium text-ml-on-surface-variant uppercase tracking-wider mb-1.5 block">Refills</Label>
                      <Input type="number" min={0} value={rxForm.refills} onChange={(e) => setRxForm(p => ({ ...p, refills: Number(e.target.value) || 0 }))} className="bg-white" />
                    </div>
                  </div>

                  {rxForm.requires_recetario_especial && (
                    <div className="rounded-md border border-amber-300 bg-amber-50/70 p-3">
                      <Label className="text-xs font-semibold text-amber-800 uppercase tracking-wider mb-1.5 block">
                        Folio del recetario especial (COFEPRIS) *
                      </Label>
                      <Input
                        value={rxForm.recetario_folio}
                        onChange={(e) => setRxForm(p => ({ ...p, recetario_folio: e.target.value }))}
                        placeholder="Código de barras / folio del recetario"
                        maxLength={50}
                        className="bg-white"
                      />
                      <p className="text-[11px] text-amber-700 mt-1.5">
                        Medicamento controlado: requiere recetario especial con código de barras.
                      </p>
                    </div>
                  )}

                  <div>
                    <Label className="text-xs font-medium text-ml-on-surface-variant uppercase tracking-wider mb-1.5 block">Indicaciones</Label>
                    <textarea
                      className="w-full min-h-[80px] rounded-md border border-input bg-white px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      value={rxForm.instructions}
                      onChange={(e) => setRxForm(p => ({ ...p, instructions: e.target.value }))}
                      placeholder="Instrucciones especiales para el paciente..."
                    />
                  </div>
                  
                  <div className="flex justify-end gap-2 pt-2">
                    {draftEditingIndex !== null && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={resetRxForm}
                        className="bg-white"
                      >
                        <X className="w-4 h-4 mr-2" />
                        Cancelar edición
                      </Button>
                    )}
                    <Button 
                      type="button" 
                      variant={draftEditingIndex !== null ? "default" : "secondary"}
                      onClick={handleSaveDraftMedication}
                      className={draftEditingIndex !== null ? "" : "bg-ml-surface-container-high hover:bg-ml-surface-container-highest text-ml-on-surface"}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      {draftEditingIndex !== null ? 'Actualizar medicamento' : 'Agregar al borrador'}
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Right column: Draft items */}
            <div className="flex flex-col h-full max-h-[600px]">
              <div className="flex items-center justify-between mb-4 px-1">
                <Label className="text-lg font-headline italic text-ml-on-surface">Borrador</Label>
                <span className="bg-ml-surface-container border border-ml-outline-variant/20 text-ml-on-surface text-xs font-bold px-3 py-1 rounded uppercase tracking-wider">
                  {rxDraftItems.length} items
                </span>
              </div>
              
              <div className="flex-1 overflow-y-auto space-y-3 pr-1 -mr-1 pb-4">
                {rxDraftItems.length === 0 ? (
                  <div className="border-2 border-dashed border-ml-outline-variant/30 rounded-xl p-8 text-center flex flex-col items-center justify-center h-48 bg-white/50">
                    <Pill className="w-10 h-10 text-ml-on-surface-variant/30 mb-3" />
                    <p className="text-sm text-ml-on-surface-variant font-medium">Aún no hay medicamentos</p>
                    <p className="text-xs text-ml-on-surface-variant/70 mt-1">Llene el formulario para agregarlos.</p>
                  </div>
                ) : (
                  rxDraftItems.map((item, index) => {
                    const isEditing = draftEditingIndex === index

                    return (
                    <div
                      key={item._key}
                      className={`flex relative rounded-xl border bg-ml-surface-container-lowest shadow-sm overflow-hidden transition-all ${
                        isEditing
                          ? 'border-ml-primary ring-2 ring-ml-primary/15 shadow-md'
                          : 'border-ml-outline-variant/20'
                      }`}
                    >
                      <div className="flex-1 min-w-0 p-4">
                        <div className="flex items-start justify-between gap-3 mb-1">
                          <p className="text-base font-semibold text-ml-on-surface truncate">{item.medication_name}</p>
                          {isEditing && (
                            <span className="shrink-0 rounded bg-ml-primary/10 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-ml-primary">
                              Editando
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-ml-on-surface-variant">{item.dosage} · {item.frequency}</p>
                        <p className="text-xs text-ml-on-surface-variant mt-0.5">{item.duration} · {item.quantity} {item.quantity_unit}</p>
                      </div>
                      <div className="flex flex-col border-l border-ml-outline-variant/10 w-12 shrink-0">
                        <button 
                          type="button" 
                          onClick={() => handleEditDraftMedication(index)} 
                          className="flex-1 flex items-center justify-center bg-ml-surface-container-low hover:bg-ml-surface-container text-ml-on-surface transition-colors"
                          title="Editar medicamento"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <div className="h-px w-full bg-ml-outline-variant/10"></div>
                        <button 
                          type="button" 
                          onClick={() => handleRemoveDraftMedication(index)} 
                          className="flex-1 flex items-center justify-center bg-red-50 hover:bg-red-100 text-red-600 transition-colors"
                          title="Quitar medicamento"
                        >
                          <Plus className="w-4 h-4 rotate-45" />
                        </button>
                      </div>
                    </div>
                  )})
                )}
              </div>
            </div>
          </div>
          
          <div className="sticky bottom-0 z-10 bg-ml-surface-container-lowest border-t border-ml-outline-variant/10 px-8 py-5 flex items-center justify-between">
            <Button variant="ghost" onClick={() => { resetPrescriptionSetEditor(); setNewRxOpen(false) }} className="text-base font-medium text-ml-on-surface-variant hover:text-ml-on-surface hover:bg-ml-surface-container-low">
              Cancelar
            </Button>
            <Button
              onClick={handleSavePrescriptionSet}
              disabled={rxDraftItems.length === 0 || creatingRx}
              className="bg-ml-primary hover:bg-ml-primary/90 text-white text-base font-medium px-8 h-12"
            >
              {creatingRx ? (
                <><Loader2 className="h-5 w-5 animate-spin mr-2" />Guardando...</>
              ) : editingPrescriptionSetId ? 'Guardar cambios' : 'Crear receta'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
